/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect, useRef } from "react";
import useForm from "react-hook-form";
import { Auth } from "@aws-amplify/auth";
import { CognitoUser } from "amazon-cognito-identity-js";
import { notify, emailValidator, getPlatformUrl } from "./utils";
import TermsAndConditions from "./LandingPage/TermsAndConditions";
import { PlatformMigrationPopup } from "./PlatformMigrationPopup";
import { Form, Button, Modal, Icon } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import cStyles from "../../components/Components.module.css";
import { fieldContainer } from "./FieldContainer";
import { FormProps } from "aws-amplify-react/props";
import { useTranslation } from "react-i18next";

type FormData = {
  username: string;
  password: string;
};

const SignIn: React.FC<FormProps> = ({
  authState = "",
  setMessage,
  onStateChange,
  onAuthEvent,
  urlParams,
  setLoading,
  loading,
}) => {
  const [showTos, setShowTos] = useState(false);
  const [showMigrationPopup, setMigrationPopup] = useState(false);
  const { register, handleSubmit, errors } = useForm<FormData>();
  const history = useHistory();
  const { t } = useTranslation(["signIn", "common"]);

  useEffect(() => {
    if (process.env.REACT_APP_SHOW_LOGIN_MIGRATION_NOTICE) {
      setMessage(
        <div className={styles.migrationMessageContainer}>
          <div className={styles.migrationSubContainer}>
            <div className={styles.iconCircle}>
              <div className={styles.iconInnerTriangle}></div>
              <div className={styles.iconOuterTriangle}></div>
              <Icon name="warning" className={styles.warningIcon} />
            </div>
            <div>
              <div className={styles.migrationHeading}>
                <h2>IMPORTANT NOTE FOR ALL APPSHIELD USERS</h2>
                <div className={styles.migrationMessage}>
                  Your account has been migrated to our Verimatrix
                  Platform!
                </div>
              </div>
            </div>
          </div>
          <Button
            size="small"
            className={styles.viewDetailsButton}
            onClick={() => {
              setMigrationPopup(true);
            }}
          >
            View Details
          </Button>
        </div>
      );
    }
    return () => {
      if (process.env.REACT_APP_SHOW_LOGIN_MIGRATION_NOTICE) {
        setMessage(null);
      }
    };
  }, []);

  const emptyInputs = {
    username: "",
    password: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const userNamePrefillRef = useRef<string>();
  if (urlParams?.userNamePrefill && !userNamePrefillRef.current) {
    userNamePrefillRef.current = urlParams.userNamePrefill;
    if (userNamePrefillRef.current) {
      inputs.username = userNamePrefillRef.current;
      history.push("/login");
    }
  }

  if (urlParams && urlParams.sessionExpired) {
    setMessage(t("signed_out"));
    history.push("/login");
  }

  const { changeState } = notify(authState, onAuthEvent, onStateChange);

  const validAuthStates = ["signIn", "signedOut", "signedUp"];
  if (!validAuthStates.includes(authState)) {
    return null;
  }

  const checkContact = (user: CognitoUser) => {
    if (!Auth || typeof Auth.verifiedContact !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }
    Auth.verifiedContact(user).then((data) => {
      if (!!Object.keys(data.verified).length) {
        changeState("signedIn", user);
      } else {
        user = Object.assign(user, data);
        changeState("verifyContact", user);
      }
    });
  };

  const handleSignInError = (err: any, data: FormData) => {
    data.username = data.username.toLocaleLowerCase();
    if (err.code === "UserNotConfirmedException") {
      changeState("confirmSignUp", { username: data.username });
      return;
    } else if (err.code === "PasswordResetRequiredException") {
      changeState("forgotPassword", { username: data.username });
      return;
    } else if (err.code === "UserLambdaValidationException") {
      if (err.message.includes("pending_approval")) {
        setMessage(t("account_pending"));
        return;
      } else if (err.message.includes("awaiting")) {
        setMessage(t("common:already_signup"));
        return;
      } else if (err.message.includes("user_disabled")) {
        setMessage(t("no_login"));
        return;
      } else if (err.message.includes("tos_approval_required")) {
        setShowTos(true);
        return;
      }
    } else if (
      err.code === "InvalidParameterException" &&
      err.message === "Invalid input given"
    ) {
      // Amplify has a open bug when handling Cognito users in RESET_REQUIRED
      // state (which is the state after CSV import). See the following issue
      // for details => https://github.com/aws-amplify/amplify-js/issues/4516.
      // Until the issue has been fixed, pretend that this error is the account
      // recovery challenge trigger, and switch to recovery view.
      changeState("forgotPassword", { username: data.username });
      return;
    }

    const migrationNotice = (
      <div>
        {t("incorrect_user_pass")}
        <br />
        Notice: your account might have already been migrated to
        <a
          href={getPlatformUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.formLink}
        >
          Verimatrix Platform.
          <br />
        </a>
        Please check your email for instructions. Need help? Contact
        appshield@verimatrix.com
      </div>
    );

    setMessage(
      process.env.REACT_APP_SHOW_LOGIN_FAILURE_MIGRATION_NOTICE
        ? migrationNotice
        : t("incorrect_user_pass")
    );
  };

  const signIn = async (data: FormData, tosAccepted = false): Promise<void> => {
    if (!Auth || typeof Auth.signIn !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setLoading(true);
    data.username = data.username.toLocaleLowerCase();
    try {
      const user = await Auth.signIn({
        username: data.username,
        password: data.password,
        ...(tosAccepted && { validationData: { tos: tosAccepted.toString() } }),
      });
      if (
        user.challengeName === "SMS_MFA" ||
        user.challengeName === "SOFTWARE_TOKEN_MFA"
      ) {
        changeState("confirmSignIn", user);
      } else if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        changeState("requireNewPassword", user);
      } else if (user.challengeName === "MFA_SETUP") {
        changeState("TOTPSetup", user);
      } else if (
        user.challengeName === "CUSTOM_CHALLENGE" &&
        user.challengeParam &&
        user.challengeParam.trigger === "true"
      ) {
        changeState("customConfirmSignIn", user);
      } else {
        checkContact(user);
      }
    } catch (err) {
      handleSignInError(err, data);
    } finally {
      setLoading(false);
    }
  };

  const onTosAccepted = () => {
    setShowTos(false);
    signIn(
      {
        username: inputs.username,
        password: inputs.password,
      },
      true
    );
  };

  const onTosRejected = () => {
    setShowTos(false);
    setMessage(t("must_accept"));
  };

  return (
    <React.Fragment>
      <Form
        onSubmit={handleSubmit((data) => signIn(data))}
        className={styles.signupForm}
      >
        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.username}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("common:email"), errors },
              {
                autoFocus:
                  window.location.pathname.toLocaleLowerCase() === "/login",
                name: "username",
                key: "username",
                placeholder: t("common:enter_email"),
                autoComplete: "username",
                ref: register({
                  required: t("common:email_required") as string,
                  pattern: {
                    value: emailValidator,
                    message: t("common:valid_email"),
                  },
                }),
                defaultValue: userNamePrefillRef.current,
                disabled: !!userNamePrefillRef.current || loading,
                onChange: handleInputChange,
              }
            )}
          </Form.Field>
        </Form.Group>

        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.password}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("common:password"), errors },
              {
                name: "password",
                type: "password",
                placeholder: t("enter_password"),
                autoComplete: "current-password",
                ref: register({
                  required: t("common:please_enter_password") as string,
                }),
                onChange: handleInputChange,
                disabled: loading,
              }
            )}
          </Form.Field>
        </Form.Group>
        <div style={{ textAlign: "right" }}>
          <span
            className={styles.formLink}
            onClick={() => {
              setMessage(null);
              changeState("forgotPassword");
            }}
            tabIndex={0}
          >
            {t("forgot_password")}
          </span>
        </div>

        <Button
          id="loginButton"
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          disabled={loading}
          loading={loading}
          tabIndex={0}
          onClick={() => {
            setMessage(null);
          }}
        >
          {t("log_in")}
        </Button>

        <div className={styles.googleNotice}>
          {t("google_rc1")}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.formLink}
            style={{ fontSize: "12px", fontWeight: 300 }}
          >
            {t("common:privacy_notice")}{" "}
          </a>
          {t("google_rc3")}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.formLink}
            style={{ fontSize: "12px", fontWeight: 300 }}
          >
            {t("google_rc4")}
          </a>{" "}
          {t("google_rc5")}
        </div>
      </Form>

      <Modal open={showTos} size="large">
        <Modal.Content image scrolling style={{ backgroundColor: "#f8f6fa" }}>
          <div style={{ maxWidth: "1000px" }}>
            <TermsAndConditions />
          </div>
        </Modal.Content>
        <Modal.Actions style={{ margin: 0 }}>
          <span
            className={styles.formLink}
            style={{ fontWeight: 500, marginRight: "1rem" }}
            onClick={onTosRejected}
          >
            {t("common:close")}
          </span>

          <Button
            style={{ borderRadius: "4px" }}
            color="green"
            onClick={onTosAccepted}
          >
            {t("common:accept")}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        open={showMigrationPopup}
        size="tiny"
        className={styles.migrationMessagePopup}
      >
        <Modal.Content image scrolling style={{ backgroundColor: "#f8f6fa" }}>
          <PlatformMigrationPopup />
        </Modal.Content>
        <Modal.Actions
          style={{ margin: 0 }}
          className={styles.migrationPopupActions}
        >
          <Button
            className={styles.closeWarningPopup}
            onClick={() => {
              setMigrationPopup(false);
            }}
          >
            {t("OK, Got it")}
          </Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
};

export default SignIn;
